import { useCallback, useMemo, useState } from "react";
import {
  fitModelWithOptions,
  modelWithExtras,
  modelWithout,
  placeModelExtra,
  type EffortScale,
  type Exercise,
  type Locale,
  type ModelChoices,
  type ModelExtra,
  type ModelLevel,
  type ModelOptionChoices,
  type ProgramFocus,
  type ProgramModel,
  type SessionMinutes,
} from "@heraion/shared";

import { buildModelFollow } from "../Library/modelCatalog";

type Model = Omit<ProgramModel, "id"> & { id?: string };

/** The wizard's choices the fit and the build read. */
export type WizardFitChoices = {
  level: ModelLevel;
  focus: ProgramFocus | undefined;
  minutes: SessionMinutes;
  perWeek: number;
  blocks: number;
  picks: Record<string, string>;
  effortScale: EffortScale;
  bySlug: ReadonlyMap<string, Exercise>;
  lang: Locale;
  /** #479: the options the athlete chose. */
  options: ModelOptionChoices;
};

// #413, #492 F3: what the wizard's days hold, rebuilt from the choices at
// every change: the model with its added exercises, less the rows taken out,
// fitted to the minutes with the rows put back kept, and what Create writes.
export function useModelWizardFit(model: Model, removed: ReadonlySet<string>, c: WizardFitChoices) {
  const { level, focus, minutes, perWeek, blocks, picks, effortScale, bySlug, lang, options } = c;
  // The rows put back: the fit cuts another in their place.
  const [pinned, setPinned] = useState<ReadonlySet<string>>(() => new Set());
  // An extra no rule writes at this level hides and stays here, back with the level (main loop's choice).
  const [extras, setExtras] = useState<readonly ModelExtra[]>([]);
  const full = useMemo(
    () => modelWithExtras(model, extras, { level, ...(focus ? { focus } : {}), exercises: bySlug }),
    [model, extras, level, focus, bySlug],
  );
  const kept = useMemo(() => modelWithout(full, removed), [full, removed]);
  // What the fit reads; Create adds the rest. One block is enough for the days.
  const fitChoices = useMemo<ModelChoices>(
    () => ({
      name: "",
      level,
      ...(focus ? { focus } : {}),
      minutes,
      sessionsPerWeek: perWeek,
      blocks: 1,
      exercisePicks: picks,
      effortScale,
      exercises: bySlug,
      pinned,
    }),
    [level, focus, minutes, perWeek, picks, effortScale, bySlug, pinned],
  );
  const fitted = useMemo(() => fitModelWithOptions(kept, fitChoices, options), [kept, fitChoices, options]);
  const fit = fitted.days;
  // The rows the fit took out whole: shown struck through, to put back.
  const cut = useMemo(
    () => new Set(fit.flatMap((d) => d.cut.filter((x) => x.wholeRow).map((x) => x.rowId))),
    [fit],
  );
  // What Follow or Create writes at these choices. No name: the model's.
  const build = useCallback(
    (name?: string) =>
      buildModelFollow(
        kept,
        {
          ...(name !== undefined ? { name } : {}),
          level,
          ...(focus ? { focus } : {}),
          minutes,
          sessionsPerWeek: perWeek,
          blocks,
          exercisePicks: picks,
          pinned,
          options,
        },
        { catalog: bySlug, effortScale, lang },
      ),
    [kept, level, focus, minutes, perWeek, blocks, picks, pinned, options, bySlug, effortScale, lang],
  );
  const keep = useCallback((id: string) => setPinned((old) => (old.has(id) ? old : new Set(old).add(id))), []);
  const place = useCallback(
    (slug: string) => placeModelExtra(model, { extras, removed, options }, fitChoices, slug, fit),
    [model, extras, removed, options, fitChoices, fit],
  );
  const add = useCallback((extra: ModelExtra) => setExtras((old) => [...old, extra]), []);
  // New at any change but an add: what a «just added» line is read against.
  const settled = useMemo(() => ({ fitChoices, removed, blocks, lang, options }), [fitChoices, removed, blocks, lang, options]);
  return { full, fit, cut, pinned, extras, build, keep, place, add, settled, ignoreOldMax: fitted.ignoreOldMax };
}

export type ModelWizardFit = ReturnType<typeof useModelWizardFit>;
